/**
 * @file limits.ts
 * @description Лимиты вида сайта (phase19.16; SB64 — 30 секций на странице,
 * 10 элементов в колонке; db-design-r1 §6 — заодно рисунки задника, колонки
 * подвала, уровни меню, автосписок категорий; SB99 / §7.12 — свои шрифты
 * магазина: число семейств, файлов и размер загрузки; Р2, db-design-r2 —
 * лимиты секции v2: рисунки, галерея, плитки, вопросы, товары и статьи,
 * надписи на фото, предохранитель размера payload; Р2а, db-design-r2a —
 * блоки шаблона страницы товара, «Похожие товары», «Отзывы о товаре»).
 *
 * Одни и те же числа читают сервер (проверка при сохранении, публикации,
 * импорте), кабинет (подсказка «до 3», выключенная кнопка «Добавить») и zod-
 * схемы этого пакета. CHECK в БД для части лимитов не годится: правило
 * захватывает несколько строк (db-design-r1 §6).
 */

export const SITE_LIMITS = {
  /** SB64: секций на одной странице. */
  sectionsPerPage: 30,
  /** SB64: элементов в одной колонке секции. */
  elementsPerColumn: 10,
  /** SB11, db-design-r1 §1.1: рисунков задника сайта. */
  backdropImages: 3,
  /** db-design-r1 §6: колонок в подвале (группы и автосписок категорий верхнего уровня). */
  footerColumns: 3,
  /** SB15: уровней меню. */
  menuDepth: 2,
  /** CHECK `SiteDraftMenuItem_target_check`: сколько категорий в автосписке. */
  catalogCategoriesAutoLimit: 24,
  /**
   * Технический предохранитель (не правило SB): контактов в настройках сайта.
   * Нужен, чтобы JSON-поле `SiteDraft.contacts` не росло без границ.
   */
  contacts: 20,
  /** §7.9 фазы (db-design-designs §2 п.11): сохранённых дизайнов на магазин. */
  designs: 20,
  /** SB99, фаза §7.12: своих шрифтов (семейств) на магазин. */
  shopFonts: 5,
  /**
   * SB99, §7.12: файлов (начертаний) в одном своём шрифте — справочно,
   * 2 веса (400/700) × 2 стиля (прямой/курсив); фактически ограничивает
   * уникальность пары «вес + стиль».
   */
  shopFontFiles: 4,
  /** SB99, §7.12: размер загружаемого файла (ttf / otf / woff2), байт. */
  shopFontUploadBytes: 2 * 1024 * 1024,
  /**
   * §7.12: woff2 после перевода тяжелее этого — мягкое предупреждение
   * «шрифт замедлит сайт на телефоне», загрузку не запрещает.
   */
  shopFontHeavyWoff2Bytes: 400 * 1024,
  /** Р2, SB23: рисунков у одной секции (как у фона страницы). */
  sectionDecor: 3,
  /** Р2: фото в элементе «Галерея». */
  galleryImages: 12,
  /** Р2: плиток в элементе «Плитки-ссылки». */
  tiles: 12,
  /** Р2: пар «вопрос — ответ» в элементе «Вопросы и ответы». */
  faqItems: 30,
  /** Р2: товаров, выбранных вручную, в элементе «Товары». */
  manualProducts: 24,
  /** Р2: статей в элементе «Статьи блога» (выбранных и показанных). */
  articles: 12,
  /** Р2: надписей поверх одного фото. */
  sectionOverlays: 2,
  /** Р2 (решение пользователя 2026-10-09): объявлений в «Строке объявлений» с ротацией. */
  announcements: 5,
  /** Р2 (решение пользователя 2026-10-10): показателей в элементе «Цифры». */
  statsItems: 4,
  /** Р2: столбцов в элементе «Таблица». */
  tableColumns: 6,
  /** Р2: строк в элементе «Таблица». */
  tableRows: 30,
  /**
   * Р2, db-design-r2 §1а: технический предохранитель размера payload секции
   * (JSON, байт UTF-8). Самый большой блок dev-базы — 2,1 КБ.
   */
  sectionPayloadBytes: 65536,
  /**
   * Р2а (db-design-r2a, ответ 2): блоков в шаблоне страницы товара. Меньше
   * 30 обычной страницы: шаблон рендерится на каждой странице товара.
   */
  productTemplateSections: 20,
  /** Р2а: сколько товаров показывает элемент «Похожие товары». */
  productRelated: 8,
  /** Р2а: сколько отзывов показывает элемент «Отзывы о товаре» (дальше — «Все отзывы»). */
  productReviews: 6,
  /**
   * Р3г (SB134): вступление баннера согласия на cookie, которое пишет
   * владелец, — в кодовых точках, простой текст без ссылок и разметки.
   */
  consentIntro: 300,
} as const;

export type SiteLimitKey = keyof typeof SITE_LIMITS;
